import { useEffect, useState, useMemo } from 'react';
import { RatingResultPayload, RatingResultEntry } from '../../../hooks/useRatingResultBroadcast';

const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";
const FONT_DECO = "'Cinzel', serif";
const FONT_HUD  = "'Orbitron', monospace";

// ── Particle types ────────────────────────────────────────────────────────────
interface Particle {
  id: number; x: number; y: number;
  dx: number; dy: number;
  color: string; size: number;
  delay: number; dur: number; rot: number;
  shape: 'circle' | 'diamond' | 'rect';
}
const P_COLORS = ['#d4a843','#f5d060','#ffffff','#7ec8a0','#ff8800','#ffd700','#ff6060','#44ddff','#c06b9a','#a78bfa'];

function genParticles(n: number): Particle[] {
  return Array.from({ length: n }, (_, i) => {
    const angle = (Math.random() * 360 * Math.PI) / 180;
    const spd   = 60 + Math.random() * 200;
    return {
      id: i,
      x: 50 + (Math.random() - 0.5) * 20,
      y: 35,
      dx: Math.cos(angle) * spd,
      dy: Math.sin(angle) * spd,
      color: P_COLORS[i % P_COLORS.length],
      size: 4 + Math.random() * 8,
      delay: Math.random() * 0.3,
      dur: 0.9 + Math.random() * 0.7,
      rot: Math.random() * 720,
      shape: (['circle', 'diamond', 'rect'] as const)[Math.floor(Math.random() * 3)],
    };
  });
}

// ── Ambient sparkles ──────────────────────────────────────────────────────────
const SPARKLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: `${5 + ((i * 47 + 11) % 90)}%`,
  size: 2.5 + (i % 3) * 1.5,
  delay: `${(i * 0.22) % 2.8}s`,
  dur: `${1.8 + (i % 4) * 0.4}s`,
  color: ['#d4a843', '#fff7a0', '#ffd700', '#e8c050', '#7ec8a0'][i % 5],
  drift: `${((i % 7) - 3) * 18}px`,
}));

// ── Top winner hero card ──────────────────────────────────────────────────────
function WinnerCard({ entry, phase }: { entry: RatingResultEntry; phase: number }) {
  const color = entry.targetTeam === 'black' ? '#d4a843' : '#e0e0e0';
  const rgb   = entry.targetTeam === 'black' ? '212,168,67' : '224,224,224';

  return (
    <div
      className="relative rounded-3xl overflow-hidden"
      style={{
        background: `linear-gradient(160deg, rgba(${rgb},0.18) 0%, rgba(${rgb},0.06) 50%, #06060f 100%)`,
        border: `2px solid rgba(${rgb},0.55)`,
        boxShadow: `0 0 50px rgba(${rgb},0.35), 0 0 100px rgba(${rgb},0.12)`,
        opacity: phase >= 2 ? 1 : 0,
        transform: phase >= 2 ? 'scale(1) translateY(0)' : 'scale(0.88) translateY(20px)',
        transition: 'all 0.65s cubic-bezier(0.34,1.4,0.64,1)',
      }}
    >
      {/* Sweep light */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 bottom-0 w-1/3"
          style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.06),transparent)', animation: 'rroSweep 2.2s linear infinite', left: '-100%' }} />
      </div>

      {/* Top glow line */}
      <div className="h-px w-full" style={{ background: `linear-gradient(90deg,transparent,rgba(${rgb},0.9),transparent)` }} />

      <div className="px-5 py-5 flex flex-col items-center gap-3">
        {/* Crown + ring */}
        <div className="relative flex items-center justify-center">
          <div className="absolute w-20 h-20 rounded-full"
            style={{ border: `1.5px solid rgba(${rgb},0.3)`, animation: 'rroRingPulse 1.8s ease-out infinite' }} />
          <div className="absolute w-28 h-28 rounded-full"
            style={{ border: `1px solid rgba(${rgb},0.12)`, animation: 'rroRingPulse 1.8s 0.5s ease-out infinite' }} />
          <div className="w-16 h-16 rounded-full flex items-center justify-center relative z-10"
            style={{
              background: `radial-gradient(circle at 35% 35%, rgba(${rgb},0.5), rgba(${rgb},0.15))`,
              border: `2px solid rgba(${rgb},0.7)`,
              boxShadow: `0 0 24px rgba(${rgb},0.6), inset 0 0 20px rgba(${rgb},0.15)`,
            }}>
            <i className="ri-vip-crown-2-fill text-3xl"
              style={{ color, filter: `drop-shadow(0 0 10px rgba(${rgb},1))` }} />
          </div>
        </div>

        {/* Champion badge */}
        <div className="flex items-center gap-2">
          <i className="ri-sparkling-2-fill text-xs" style={{ color, animation: 'rroSpk 0.8s ease-in-out infinite' }} />
          <span className="text-[10px] font-black tracking-[0.5em] uppercase"
            style={{ color: `rgba(${rgb},0.6)`, fontFamily: FONT_DECO }}>Champion</span>
          <i className="ri-sparkling-2-fill text-xs" style={{ color, animation: 'rroSpk 0.8s 0.2s ease-in-out infinite' }} />
        </div>

        {/* Name */}
        <p className="text-2xl font-black text-center leading-tight"
          style={{
            color: 'white',
            fontFamily: FONT_BODY,
            textShadow: `0 0 30px rgba(${rgb},0.9), 0 0 60px rgba(${rgb},0.4)`,
          }}>
          {entry.targetName}
        </p>

        {/* Score */}
        <div className="flex items-end gap-2">
          <span className="font-black leading-none tabular-nums"
            style={{ fontSize: '3.2rem', color, fontFamily: FONT_HUD, textShadow: `0 0 20px rgba(${rgb},0.8)` }}>
            {entry.totalScore}
          </span>
          <div className="pb-2 flex flex-col items-start gap-1">
            <span className="text-[11px] font-black" style={{ color: `rgba(${rgb},0.6)`, fontFamily: FONT_BODY }}>總分</span>
            <span className="text-[10px]" style={{ color: `rgba(${rgb},0.4)`, fontFamily: FONT_BODY }}>
              均 {entry.avgScore.toFixed(1)} · {entry.voteCount}票
            </span>
          </div>
        </div>

        {/* Stars */}
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map(s => (
            <i key={s}
              className={s <= Math.round(entry.avgScore) ? 'ri-star-fill' : 'ri-star-line'}
              style={{
                fontSize: '1rem',
                color: s <= Math.round(entry.avgScore) ? color : `rgba(${rgb},0.15)`,
                filter: s <= Math.round(entry.avgScore) ? `drop-shadow(0 0 4px rgba(${rgb},0.8))` : 'none',
              }} />
          ))}
        </div>
      </div>

      {/* Bottom glow line */}
      <div className="h-px w-full" style={{ background: `linear-gradient(90deg,transparent,rgba(${rgb},0.3),transparent)` }} />
    </div>
  );
}

// ── Runner-up card ────────────────────────────────────────────────────────────
function RunnerCard({ entry, rank, delay }: { entry: RatingResultEntry; rank: number; delay: number }) {
  const [show, setShow] = useState(false);
  const color = entry.targetTeam === 'black' ? '#d4a843' : '#e0e0e0';
  const rgb   = entry.targetTeam === 'black' ? '212,168,67' : '224,224,224';

  useEffect(() => {
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div className="flex items-center gap-3 rounded-2xl px-4 py-3"
      style={{
        background: `linear-gradient(135deg, rgba(${rgb},0.07), rgba(${rgb},0.02))`,
        border: `1px solid rgba(${rgb},0.18)`,
        opacity: show ? 1 : 0,
        transform: show ? 'translateX(0)' : 'translateX(-20px)',
        transition: 'all 0.45s cubic-bezier(0.22,1,0.36,1)',
      }}>
      {/* Rank badge */}
      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
        style={{ background: `rgba(${rgb},0.1)`, border: `1px solid rgba(${rgb},0.25)` }}>
        <span className="font-black text-sm" style={{ color, fontFamily: FONT_HUD }}>{rank}</span>
      </div>

      {/* Name */}
      <span className="text-sm font-black flex-1 truncate" style={{ color: '#bbb', fontFamily: FONT_BODY }}>
        {entry.targetName}
      </span>

      {/* Stars + score */}
      <div className="flex items-center gap-2 shrink-0">
        <div className="flex items-center gap-px">
          {[1, 2, 3, 4, 5].map(s => (
            <i key={s}
              className={`${s <= Math.round(entry.avgScore) ? 'ri-star-fill' : 'ri-star-line'} text-[10px]`}
              style={{ color: s <= Math.round(entry.avgScore) ? color : `rgba(${rgb},0.15)` }} />
          ))}
        </div>
        <span className="text-base font-black tabular-nums" style={{ color, fontFamily: FONT_HUD }}>
          {entry.totalScore}
        </span>
      </div>
    </div>
  );
}

// ── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ label, color, rgb, phase }: { label: string; color: string; rgb: string; phase: number }) {
  return (
    <div className="flex items-center gap-2"
      style={{ opacity: phase >= 3 ? 1 : 0, transition: 'opacity 0.4s 0.1s' }}>
      <span className="text-[9px] font-black tracking-[0.4em] uppercase"
        style={{ color, fontFamily: FONT_DECO }}>{label}</span>
      <div className="h-px flex-1" style={{ background: `rgba(${rgb},0.2)` }} />
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
interface Props {
  result: RatingResultPayload;
  onDismiss: () => void;
  isJudge?: boolean;
}

export default function RatingResultOverlay({ result, onDismiss, isJudge = false }: Props) {
  const [phase, setPhase] = useState(0);
  const [particles, setParticles] = useState<Particle[]>([]);

  useEffect(() => {
    // Staggered phase entrance
    const t1 = setTimeout(() => setPhase(1), 80);
    const t2 = setTimeout(() => { setPhase(2); setParticles(genParticles(70)); }, 400);
    const t3 = setTimeout(() => setPhase(3), 900);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const sorted = useMemo(() =>
    [...result.entries].sort((a, b) => b.totalScore - a.totalScore), [result.entries]);
  const maxTotal = sorted[0]?.totalScore ?? 1;
  const winner   = sorted[0];
  const rest     = sorted.slice(1);
  const isIndividual = result.votingType === 'individual';

  const blackRest = rest.filter(e => e.targetTeam === 'black');
  const whiteRest = rest.filter(e => e.targetTeam === 'white');

  const renderRestGroup = (entries: RatingResultEntry[], allRest: RatingResultEntry[], label: string, color: string, rgb: string) => {
    if (entries.length === 0) return null;
    return (
      <div className="space-y-1.5">
        <SectionLabel label={label} color={color} rgb={rgb} phase={phase} />
        {entries.map(e => {
          const rankInAll = rest.indexOf(e);
          return (
            <RunnerCard
              key={e.targetId}
              entry={e}
              rank={sorted.indexOf(e) + 1}
              delay={rankInAll * 120}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center overflow-hidden"
      style={{
        background: 'rgba(0,0,0,0.92)',
        backdropFilter: 'blur(10px)',
        opacity: phase >= 1 ? 1 : 0,
        transition: 'opacity 0.3s',
      }}>

      {/* ── Ambient radial glow ── */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 30%, rgba(212,168,67,0.08) 0%, transparent 70%)' }} />

      {/* ── Particles ── */}
      {particles.map(p => (
        <div key={p.id} className="absolute pointer-events-none z-20"
          style={{
            left: `${p.x}%`, top: `${p.y}%`,
            width: p.size, height: p.size,
            background: p.color,
            borderRadius: p.shape === 'circle' ? '50%' : p.shape === 'diamond' ? '2px' : '1px',
            transform: p.shape === 'diamond' ? 'translate(-50%,-50%) rotate(45deg)' : 'translate(-50%,-50%)',
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            '--dx': `${p.dx}px`,
            '--dy': `${p.dy}px`,
            '--rot': `${p.rot}deg`,
            animation: `rroPart ${p.dur}s ${p.delay}s cubic-bezier(0.15,0.8,0.3,1) forwards`,
          } as React.CSSProperties}
        />
      ))}

      {/* ── Floating sparkles ── */}
      {phase >= 2 && SPARKLES.map(s => (
        <div key={s.id} className="absolute pointer-events-none z-10 rounded-full"
          style={{
            left: s.left, bottom: 0,
            width: s.size, height: s.size,
            background: s.color,
            boxShadow: `0 0 ${s.size * 2}px ${s.color}`,
            '--drift': s.drift,
            animation: `rroSparkle ${s.dur} ${s.delay} ease-in-out infinite`,
            opacity: 0,
          } as React.CSSProperties}
        />
      ))}

      {/* ── Sheet ── */}
      <div
        className="relative w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col z-30"
        style={{
          background: 'linear-gradient(180deg, #0e0b18 0%, #07060f 100%)',
          border: '1px solid rgba(212,168,67,0.25)',
          boxShadow: '0 -20px 80px rgba(212,168,67,0.18), 0 0 120px rgba(0,0,0,0.9)',
          transform: phase >= 1 ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.5s cubic-bezier(0.22,1,0.36,1)',
          maxHeight: '90dvh',
        }}
      >
        {/* Scan beam top */}
        <div className="absolute top-0 left-0 right-0 h-px overflow-hidden z-10">
          <div className="h-full" style={{ background: 'linear-gradient(90deg,transparent,rgba(212,168,67,0.9),transparent)', animation: 'rroScan 2.5s linear infinite' }} />
        </div>

        {/* Drag handle */}
        <div className="flex justify-center pt-3 sm:hidden">
          <div className="w-10 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.15)' }} />
        </div>

        {/* ── Header ── */}
        <div className="px-5 pt-4 pb-3 flex items-center gap-3"
          style={{
            opacity: phase >= 1 ? 1 : 0,
            transform: phase >= 1 ? 'translateY(0)' : 'translateY(-8px)',
            transition: 'all 0.4s 0.1s',
          }}>
          {/* Icon */}
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 relative"
            style={{ background: 'rgba(212,168,67,0.15)', border: '1px solid rgba(212,168,67,0.4)', boxShadow: '0 0 20px rgba(212,168,67,0.25)' }}>
            <div className="absolute inset-0 rounded-2xl" style={{ border: '1px solid rgba(212,168,67,0.2)', animation: 'rroRingPulse 1.8s ease-out infinite' }} />
            <i className="ri-trophy-2-fill text-lg"
              style={{ color: '#d4a843', filter: 'drop-shadow(0 0 6px rgba(212,168,67,0.8))' }} />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-[9px] font-black tracking-[0.4em] uppercase"
              style={{ color: 'rgba(212,168,67,0.45)', fontFamily: FONT_DECO }}>
              {result.votingType === 'team' ? '隊伍模式' : '個人模式'}  ·  評分結果
            </p>
            <p className="text-sm font-black truncate text-white" style={{ fontFamily: FONT_BODY }}>
              {result.title}
            </p>
          </div>

          {/* Voter count */}
          <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl shrink-0"
            style={{ background: 'rgba(126,200,160,0.08)', border: '1px solid rgba(126,200,160,0.2)' }}>
            <i className="ri-group-fill text-[10px]" style={{ color: '#7ec8a0' }} />
            <span className="text-[10px] font-black" style={{ color: '#7ec8a0', fontFamily: FONT_HUD }}>
              {result.totalVoters}票
            </span>
          </div>
        </div>

        {/* Gold divider */}
        <div className="mx-5 h-px mb-4" style={{ background: 'linear-gradient(90deg,transparent,rgba(212,168,67,0.2),transparent)' }} />

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-5 space-y-4 pb-4" style={{ minHeight: 0 }}>
          {sorted.length === 0 ? (
            <div className="flex flex-col items-center py-10 gap-3">
              <i className="ri-bar-chart-2-line text-3xl" style={{ color: 'rgba(212,168,67,0.2)' }} />
              <p className="text-xs text-center" style={{ color: '#2a3a5a', fontFamily: FONT_BODY }}>無評分資料</p>
            </div>
          ) : (
            <>
              {/* Winner hero */}
              {winner && <WinnerCard entry={winner} phase={phase} />}

              {/* Runner-up section */}
              {rest.length > 0 && (
                <div
                  style={{
                    opacity: phase >= 3 ? 1 : 0,
                    transition: 'opacity 0.4s',
                  }}>
                  {isIndividual ? (
                    <div className="space-y-3">
                      {blackRest.length > 0 && renderRestGroup(blackRest, rest, '黑隊', '#d4a843', '212,168,67')}
                      {whiteRest.length > 0 && renderRestGroup(whiteRest, rest, '白隊', '#e0e0e0', '224,224,224')}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {rest.map((e, i) => (
                        <RunnerCard key={e.targetId} entry={e} rank={sorted.indexOf(e) + 1} delay={i * 120} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-5 py-4" style={{ borderTop: '1px solid rgba(212,168,67,0.1)', background: 'rgba(0,0,0,0.3)' }}>
          <button
            onClick={onDismiss}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-black cursor-pointer whitespace-nowrap relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(212,168,67,0.2), rgba(212,168,67,0.08))',
              color: '#d4a843',
              border: '1.5px solid rgba(212,168,67,0.4)',
              boxShadow: '0 0 20px rgba(212,168,67,0.15)',
              fontFamily: FONT_BODY,
            }}>
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: 'linear-gradient(90deg,transparent,rgba(212,168,67,0.12),transparent)', animation: 'rroSweep 2s linear infinite', left: '-100%' }} />
            <i className="ri-check-double-line text-base" />
            {isJudge ? '關閉公告' : '太棒了！知道了'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes rroScan { from{transform:translateX(-100%)} to{transform:translateX(100%)} }
        @keyframes rroSweep { 0%{left:-100%;opacity:0} 20%{opacity:1} 80%{opacity:1} 100%{left:200%;opacity:0} }
        @keyframes rroRingPulse {
          0%{transform:scale(1);opacity:0.7}
          100%{transform:scale(2);opacity:0}
        }
        @keyframes rroPart {
          0%{opacity:1;transform:translate(-50%,-50%) translate(0,0) scale(1) rotate(0deg)}
          60%{opacity:0.8}
          100%{opacity:0;transform:translate(-50%,-50%) translate(var(--dx),var(--dy)) scale(0.05) rotate(var(--rot))}
        }
        @keyframes rroSparkle {
          0%{opacity:0;transform:translateY(0) translateX(0) scale(0.4)}
          15%{opacity:0.9}
          80%{opacity:0.4}
          100%{opacity:0;transform:translateY(-65vh) translateX(var(--drift)) scale(0.1)}
        }
        @keyframes rroSpk {
          0%,100%{opacity:0.3;transform:scale(0.7)}
          50%{opacity:1;transform:scale(1.4)}
        }
      `}</style>
    </div>
  );
}
